/* tutor-3 (C.6, P11): EL ARRASTRE QUE CIERRA LAS HOJAS DEL TELEFONO Y EL FRENO DEL "JALAR PARA REFRESCAR".

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   Lo que vio Carlos: en Safari del iPhone, deslizar hacia abajo una hoja no la cerraba; Safari lo tomaba como "jalar
   para refrescar" y recargaba la pagina. La rayita gris prometia un gesto que no existia.

   - EL ARRASTRE (`useArrastreDeHoja`): el blanco es la FILA entera de la agarradera (un area de 44 de alto a todo lo
     ancho, `touch-action: none`), con eventos `pointer*`. Bajar mueve la hoja con el dedo (la caja, por su
     `transform`, sin repintar React); soltar con mas de 80 bajados, o con un tiron rapido, CIERRA (`alCerrar`); si
     no, la hoja regresa a su sitio. Los umbrales viven en una funcion pura (`decidirCierre`).
   - EL FRENO (`useFrenoDelRefresco`): mientras una hoja esta abierta, `overscroll-behavior: none` en `html` (se
     restaura al cerrar la ultima). El cuerpo que se desplaza y el velo llevan `overscroll-behavior: contain` en
     clase (cada hoja la suya). La app es un WKWebView: el mismo CSS.

   marca-1 (#726), "la barrita gris de TODAS las hojas": Carlos conto las hojas con agarradera y en la de las fotos y
   en la de borrar la cuenta de invitado la barrita no hacia nada (y la de borrar aparecia de golpe). La regla unica:
   TODA hoja con agarradera cierra al bajarla 80 o con un tiron, entra en 500 ms y sale en 300 (desde 768 la ventana
   se desvanece en 200), sin animacion con "reducir movimiento", y con el freno del refresco. Por eso viven AQUI,
   para todas, las cadenas de la entrada y la salida y la copia muda que pinta la salida (eran de `Ventana.js`, que
   las importa), y dos piezas nuevas:
   - `useHojaDeLaCasa(alCerrar, { abierta, cerrable })`: el arrastre, el freno y la salida de una hoja. `abierta`
     sirve a las que no se desmontan al cerrar (pintan nada con `abierta` en falso); `cerrable` en falso deja la
     hoja quieta mientras su "x" tambien lo esta (por ejemplo, a media peticion): la hoja regresa.
   - `FilaDeAgarradera`: la barrita y su area invisible de 44 a todo lo ancho, la misma de `Ventana`. */
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

export const UMBRAL_PX = 80;
/** Un tiron: al menos 16 bajados a mas de 0.5 px por milisegundo en los ultimos 100 ms. */
export const TIRON_MINIMO_PX = 16;
export const VELOCIDAD_DE_TIRON = 0.5;
const VENTANA_DE_VELOCIDAD_MS = 100;
const REGRESO_MS = 200;

/** Cierra si bajo mas de 80, o si fue un tiron rapido hacia abajo. `velocidad` en px/ms (positiva = hacia abajo). */
export function decidirCierre(bajado, velocidad) {
  if (bajado > UMBRAL_PX) return true;
  return bajado >= TIRON_MINIMO_PX && velocidad >= VELOCIDAD_DE_TIRON;
}

/** La velocidad hacia abajo de las muestras `[{y, t}]` de los ultimos 100 ms (px/ms). */
export function velocidadDe(muestras) {
  if (!muestras || muestras.length < 2) return 0;
  const ultima = muestras[muestras.length - 1];
  let primera = muestras[0];
  for (let i = muestras.length - 1; i >= 0; i -= 1) {
    if (ultima.t - muestras[i].t > VENTANA_DE_VELOCIDAD_MS) break;
    primera = muestras[i];
  }
  const dt = ultima.t - primera.t;
  return dt > 0 ? (ultima.y - primera.y) / dt : 0;
}

function ahora() {
  return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
}

/** Los manejadores para la fila de la agarradera. `cajaRef` es la hoja que se mueve. */
export function useArrastreDeHoja(cajaRef, alCerrar) {
  const cerrarRef = useRef(alCerrar);
  cerrarRef.current = alCerrar;
  const gestoRef = useRef(null);

  const mover = (px, conTransicion) => {
    const caja = cajaRef.current;
    if (!caja) return;
    caja.style.transition = conTransicion ? `transform ${REGRESO_MS}ms ease-out` : 'none';
    caja.style.transform = px > 0 ? `translateY(${px}px)` : '';
  };

  const onPointerDown = useCallback((e) => {
    if (e.button != null && e.button !== 0) return;
    gestoRef.current = { inicio: e.clientY, muestras: [{ y: e.clientY, t: ahora() }], bajado: 0, id: e.pointerId };
    const el = e.currentTarget;
    if (el && typeof el.setPointerCapture === 'function' && e.pointerId != null) {
      try { el.setPointerCapture(e.pointerId); } catch (err) { /* sin captura: el gesto sigue */ }
    }
  }, []);

  const onPointerMove = useCallback((e) => {
    const g = gestoRef.current;
    if (!g) return;
    g.bajado = Math.max(0, e.clientY - g.inicio);
    g.muestras.push({ y: e.clientY, t: ahora() });
    if (g.muestras.length > 20) g.muestras.shift();
    mover(g.bajado, false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const soltar = useCallback((cancelado, e) => {
    const g = gestoRef.current;
    gestoRef.current = null;
    if (!g) return;
    // la muestra de cuando se suelta: una pausa antes de soltar no es un tiron
    if (e && typeof e.clientY === 'number') {
      g.bajado = Math.max(0, e.clientY - g.inicio);
      g.muestras.push({ y: e.clientY, t: ahora() });
    }
    // marca-1 (#726): sin quien cierre (una hoja que en ese momento no se puede cerrar), la hoja regresa
    if (!cancelado && cerrarRef.current && decidirCierre(g.bajado, velocidadDe(g.muestras))) {
      cerrarRef.current();
      return;
    }
    mover(0, true);
    const caja = cajaRef.current;
    if (caja) window.setTimeout(() => { if (caja.style.transform === '') caja.style.transition = ''; }, REGRESO_MS + 20);
  }, [cajaRef]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: useCallback((e) => soltar(false, e), [soltar]),
    onPointerCancel: useCallback(() => soltar(true, null), [soltar]),
    style: { touchAction: 'none' },
    'data-hoja-agarradera': '',
  };
}

let abiertas = 0;
let antesDelFreno = '';

/** Mientras `activo`: `overscroll-behavior: none` en `html` (con cuenta, para varias hojas a la vez). Con
 *  `soloTelefono`, solo abajo de 768 (desde ahi la ventana va centrada y nada cambia). Con `veloRef`, ademas el velo
 *  en `contain` por estilo (asi la clase del velo de `alumno/Ventana` queda como la de la casa, que una guarda fija). */
export function useFrenoDelRefresco(activo, soloTelefono = false, veloRef = null) {
  useEffect(() => {
    if (!activo || typeof document === 'undefined') return undefined;
    if (soloTelefono) {
      const mm = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(max-width: 767px)') : null;
      if (!mm || !mm.matches) return undefined;
    }
    const html = document.documentElement;
    if (abiertas === 0) antesDelFreno = html.style.overscrollBehavior || '';
    abiertas += 1;
    html.style.overscrollBehavior = 'none';
    const velo = veloRef && veloRef.current;
    if (velo) velo.style.overscrollBehavior = 'contain';
    return () => {
      abiertas = Math.max(0, abiertas - 1);
      if (abiertas === 0) html.style.overscrollBehavior = antesDelFreno;
    };
  }, [activo, soloTelefono, veloRef]);
}

// marca-1 (#726): la entrada y la salida de las hojas de la casa (las cadenas completas: Tailwind solo genera lo que
// ve escrito). Los tiempos de smoke-alumno S2: la hoja sube en 500 ms y baja en 300; desde 768 la ventana se
// desvanece y crece de 95 % en 200. El velo se desvanece con ella. Con "reducir movimiento", nada.
export const ENTRA_EL_VELO = 'animate-in fade-in-0 motion-reduce:animate-none';
export const ENTRA_LA_CAJA = 'animate-in slide-in-from-bottom duration-500 ease-in-out md:slide-in-from-bottom-0 md:fade-in-0 md:zoom-in-95 md:duration-200 md:ease-out motion-reduce:animate-none';
export const SALE_EL_VELO = 'animate-out fade-out-0 fill-mode-forwards';
export const SALE_LA_CAJA = 'animate-out slide-out-to-bottom duration-300 ease-in-out md:slide-out-to-bottom-0 md:fade-out-0 md:zoom-out-95 md:duration-200 md:ease-out fill-mode-forwards';
export const SALIDA_MS = 300;

function sinTokens(el, cadena) {
  cadena.split(' ').forEach((c) => el.classList.remove(c));
}

/** La copia muda que pinta la salida (el velo clonado, con la caja como su primer hijo). Sin `matchMedia` o con
 *  "reducir movimiento", no hace nada. */
export function pintarLaSalida(velo, caja) {
  try {
    if (!velo || !caja || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const desplazada = caja.querySelector('[data-ventana-cuerpo]');
    const arriba = desplazada ? desplazada.scrollTop : 0;
    const copia = velo.cloneNode(true);
    copia.setAttribute('data-ventana-saliendo', '');
    copia.setAttribute('aria-hidden', 'true');
    copia.setAttribute('inert', '');
    copia.style.pointerEvents = 'none';
    copia.querySelectorAll('[data-testid],[id],[role],[aria-modal],[aria-labelledby]').forEach((n) => {
      n.removeAttribute('data-testid');
      n.removeAttribute('id');
      n.removeAttribute('role');
      n.removeAttribute('aria-modal');
      n.removeAttribute('aria-labelledby');
    });
    copia.removeAttribute('data-testid');
    sinTokens(copia, ENTRA_EL_VELO);
    SALE_EL_VELO.split(' ').forEach((c) => copia.classList.add(c));
    const cajaCopia = copia.firstElementChild;
    if (cajaCopia) {
      sinTokens(cajaCopia, ENTRA_LA_CAJA);
      SALE_LA_CAJA.split(' ').forEach((c) => cajaCopia.classList.add(c));
    }
    document.body.appendChild(copia);
    const cuerpoCopia = copia.querySelector('[data-ventana-cuerpo]');
    if (cuerpoCopia && arriba) cuerpoCopia.scrollTop = arriba;
    window.setTimeout(() => { copia.remove(); }, SALIDA_MS + 20);
  } catch (e) { /* sin salida animada: la hoja ya se fue */ }
}

/** marca-1 (#726): todo lo de una hoja con agarradera en un gancho. Devuelve `veloRef` (el fondo), `cajaRef` (la
 *  hoja, primer hijo del fondo) y los manejadores de la agarradera. El velo y la caja llevan ENTRA_EL_VELO y
 *  ENTRA_LA_CAJA en su clase. */
export function useHojaDeLaCasa(alCerrar, { abierta = true, cerrable = true } = {}) {
  const veloRef = useRef(null);
  const cajaRef = useRef(null);
  const ultimas = useRef(null);
  // las ultimas cajas pintadas: una hoja que cambia de cara sin cerrarse sale con la cara que se veia
  useLayoutEffect(() => {
    if (veloRef.current && cajaRef.current) ultimas.current = [veloRef.current, cajaRef.current];
  });
  useLayoutEffect(() => {
    if (!abierta) return undefined;
    return () => {
      const par = ultimas.current;
      ultimas.current = null;
      if (par) pintarLaSalida(par[0], par[1]);
    };
  }, [abierta]);
  useFrenoDelRefresco(abierta, true, veloRef);
  const agarradera = useArrastreDeHoja(cajaRef, cerrable ? alCerrar : null);
  return { veloRef, cajaRef, agarradera };
}

/** marca-1 (#726): la fila de la agarradera de la casa (la de `Ventana`): la barrita y, encima, el area invisible de
 *  44 a todo lo ancho que recibe el arrastre. `clase` es la de la fila de cada hoja (posicionada, para que el area se mida desde ella). */
export function FilaDeAgarradera({ agarradera, testid, clase = 'md:hidden relative flex justify-center pt-2.5' }) {
  return (
    <div className={clase} aria-hidden="true">
      <span className="w-10 h-1 rounded-full bg-[#3A3A3A]" />
      <span className="absolute inset-x-0 top-0 h-11 z-[1]" {...agarradera} data-testid={testid} />
    </div>
  );
}
